import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  Bell, 
  Clock, 
  Mail, 
  Smartphone, 
  User, 
  MessageSquare, 
  CheckCircle, 
  AlertCircle,
  Settings as SettingsIcon,
  Moon,
  Sun
} from 'lucide-react';

const NotificationSettings = ({ user }) => {
  const [preferences, setPreferences] = useState({
    // Global delivery methods
    real_time_notifications: true,
    email_notifications: true,
    push_notifications: false,
    // Task notifications - granular
    task_assigned_email: true,
    task_due_soon_email: true,
    task_due_today_email: true,
    task_overdue_email: true,
    task_completed_email: true,
    task_status_changed_email: true,
    task_priority_changed_email: false,
    
    // Collaboration
    comment_added_email: true,
    mentioned_in_comment_email: true,
    subtask_created_email: false,
    task_watched_email: true,
    
    // Team
    team_member_added_email: true,
    team_member_removed_email: false,
    
    // Project notifications
    project_created_email: false,
    project_completed_email: true,
    project_status_changed_email: true,
    
    // Messages
    new_message_email: true,
    client_portal_activity_email: true,
    
    // Meetings
    meeting_reminder_email: true,
    meeting_recording_ready_email: true,
    
    // System
    system_maintenance_email: true,
    security_alerts_email: true,
    billing_notifications_email: true,
    integration_connected_email: true,
    integration_failed_email: true,
    
    // Digest options
    daily_digest_enabled: false,
    weekly_digest_enabled: true,
    digest_delivery_time: "09:00:00",
    
    // Quiet hours
    quiet_hours_enabled: false,
    quiet_hours_start: "22:00:00",
    quiet_hours_end: "08:00:00"
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPreferences = async () => {
    try {
      const response = await api.get('/user/notification-preferences');
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (updatedPreferences) => {
    setSaving(true);
    try {
      const response = await api.put('/user/notification-preferences', updatedPreferences);
      // Update with server response to ensure sync
      if (response.data && response.data.preferences) {
        setPreferences(response.data.preferences);
      } else {
        setPreferences(updatedPreferences);
      }
      // Show brief success indicator (optional - you could add a toast notification)
      console.log('✅ Preferences saved successfully');
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const createTestNotification = async () => {
    try {
      await api.post('/notifications/test', {});
      alert('Test notification created successfully!');
    } catch (error) {
      console.error('Failed to create test notification:', error);
      alert('Failed to create test notification');
    }
  };

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const handleToggle = (key, value) => {
    const updatedPreferences = { ...preferences, [key]: value };
    savePreferences(updatedPreferences);
  };

  const handleTimeChange = (key, value) => {
    const updatedPreferences = { ...preferences, [key]: value };
    savePreferences(updatedPreferences);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const notificationGroups = [
    {
      title: 'Task Notifications',
      icon: CheckCircle,
      description: 'Get notified about task assignments, updates, and deadlines',
      settings: [
        { key: 'task_assigned_email', label: 'Task assigned to me', icon: User },
        { key: 'task_due_soon_email', label: 'Task due in 24 hours', icon: Clock },
        { key: 'task_due_today_email', label: 'Task due today', icon: AlertCircle },
        { key: 'task_overdue_email', label: 'Task is overdue', icon: AlertCircle },
        { key: 'task_completed_email', label: 'Task marked as complete', icon: CheckCircle },
        { key: 'task_status_changed_email', label: 'Task status changed', icon: SettingsIcon },
        { key: 'task_priority_changed_email', label: 'Task priority changed', icon: AlertCircle }
      ]
    },
    {
      title: 'Collaboration',
      icon: MessageSquare,
      description: 'Stay updated on comments, mentions, and team activities',
      settings: [
        { key: 'comment_added_email', label: 'New comments on my tasks', icon: MessageSquare },
        { key: 'mentioned_in_comment_email', label: 'Mentioned in comments', icon: User },
        { key: 'subtask_created_email', label: 'Subtasks added to my tasks', icon: CheckCircle },
        { key: 'task_watched_email', label: 'Updates on watched tasks', icon: Bell }
      ]
    },
    {
      title: 'Team Updates',
      icon: User,
      description: 'Get notified about team changes and member activities',
      settings: [
        { key: 'team_member_added_email', label: 'New team members added', icon: User },
        { key: 'team_member_removed_email', label: 'Team members removed', icon: User }
      ]
    },
    {
      title: 'Project Updates',
      icon: CheckCircle,
      description: 'Stay updated on project status and milestones',
      settings: [
        { key: 'project_created_email', label: 'New projects created', icon: CheckCircle },
        { key: 'project_completed_email', label: 'Projects completed', icon: CheckCircle },
        { key: 'project_status_changed_email', label: 'Project status changed', icon: SettingsIcon }
      ]
    },
    {
      title: 'Messages & Communication',
      icon: MessageSquare,
      description: 'Get notified about messages and client activity',
      settings: [
        { key: 'new_message_email', label: 'New messages', icon: MessageSquare },
        { key: 'client_portal_activity_email', label: 'Client portal activity', icon: User }
      ]
    },
    {
      title: 'Meetings & Schedule',
      icon: Clock,
      description: 'Reminders for meetings and appointments',
      settings: [
        { key: 'meeting_reminder_email', label: 'Meeting reminders', icon: Clock },
        { key: 'meeting_recording_ready_email', label: 'Meeting recordings ready', icon: CheckCircle }
      ]
    },
    {
      title: 'System & Integrations',
      icon: SettingsIcon,
      description: 'Important system updates and integration status',
      settings: [
        { key: 'integration_connected_email', label: 'New integrations connected', icon: CheckCircle },
        { key: 'integration_failed_email', label: 'Integration connection failed', icon: AlertCircle },
        { key: 'system_maintenance_email', label: 'System maintenance updates', icon: SettingsIcon },
        { key: 'security_alerts_email', label: 'Security alerts', icon: AlertCircle },
        { key: 'billing_notifications_email', label: 'Billing notifications', icon: AlertCircle }
      ]
    }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <Bell className="w-5 h-5" />
            <span>Notification Settings</span>
          </h2>
          <p className="text-gray-600">Manage how and when you receive notifications</p>
        </div>
        
        <button
          onClick={createTestNotification}
          className="px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
        >
          Send Test Notification
        </button>
      </div>

      <div className="space-y-8">
        {/* Delivery Methods */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Delivery Methods</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Bell className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-gray-900">Real-time</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Get instant notifications in the app</p>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={preferences.real_time_notifications}
                  onChange={(e) => handleToggle('real_time_notifications', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-700">Enable real-time notifications</span>
              </label>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Mail className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-900">Email</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Receive notifications via email</p>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={preferences.email_notifications}
                  onChange={(e) => handleToggle('email_notifications', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-900 font-medium">Enable email notifications</span>
              </label>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Smartphone className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">Push</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">Get push notifications on mobile</p>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={preferences.push_notifications}
                  onChange={(e) => handleToggle('push_notifications', e.target.checked)}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <span className="ml-2 text-sm text-gray-900 font-medium">Enable push notifications</span>
              </label>
            </div>
          </div>
        </div>

        {/* Notification Types */}
        {notificationGroups.map((group) => {
          const GroupIcon = group.icon;
          return (
            <div key={group.title}>
              <div className="flex items-center space-x-2 mb-4">
                <GroupIcon className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900">{group.title}</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">{group.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {group.settings.map((setting) => {
                  const SettingIcon = setting.icon;
                  return (
                    <label key={setting.key} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences[setting.key]}
                        onChange={(e) => handleToggle(setting.key, e.target.checked)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <SettingIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-900 font-medium">{setting.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Quiet Hours */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Moon className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Quiet Hours</h3>
          </div>
          <p className="text-gray-600 text-sm mb-4">Disable notifications during specific hours</p>
          
          <div className="space-y-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={preferences.quiet_hours_enabled}
                onChange={(e) => handleToggle('quiet_hours_enabled', e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-900 font-medium">Enable quiet hours</span>
            </label>

            {preferences.quiet_hours_enabled && (
              <div className="grid grid-cols-2 gap-4 ml-7">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start time</label>
                  <input
                    type="time"
                    value={preferences.quiet_hours_start}
                    onChange={(e) => handleTimeChange('quiet_hours_start', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End time</label>
                  <input
                    type="time"
                    value={preferences.quiet_hours_end}
                    onChange={(e) => handleTimeChange('quiet_hours_end', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Digest Options */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Sun className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Digest Notifications</h3>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Coming Soon</span>
          </div>
          <p className="text-gray-600 text-sm mb-4">Receive summary notifications at scheduled intervals</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-50">
            <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-not-allowed">
              <input
                type="checkbox"
                disabled
                className="w-4 h-4 text-gray-300 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500">Daily digest</span>
            </label>
            <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg cursor-not-allowed">
              <input
                type="checkbox"
                disabled
                className="w-4 h-4 text-gray-300 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-500">Weekly digest</span>
            </label>
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg">
          Saving preferences...
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;